import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from '../context/ToastContext';
import Button from './Button';
import { Save, Users, ClipboardList, Crown } from 'lucide-react';
import { NOTIFICATION_TYPES, NotificationTypeMeta } from '../constants/notifications';

interface NotificationPreference {
  notification_type: string;
  enabled: boolean;
}

// NOTIFICATION_TYPES stays the shared source of truth for labels/descriptions
// (kept in sync with the public unsubscribe page). The section grouping below is
// an in-app presentation concern, so it lives here and references types by id.
const typesById = new Map(NOTIFICATION_TYPES.map((t) => [t.id, t]));

const NOTIFICATION_GROUPS = [
  {
    title: 'Team',
    icon: Users,
    description: 'Updates about your team membership',
    typeIds: ['team_added_email'],
  },
  {
    title: 'Lineups & Matches',
    icon: ClipboardList,
    description: 'Lineup updates and reminders to confirm your spot',
    typeIds: ['lineup_notification', 'lineup_confirmation_reminder'],
  },
  {
    title: 'Captain',
    icon: Crown,
    description: 'Captain invitations and lineup-publishing reminders',
    typeIds: ['captain_invitation', 'captain_publish_reminder'],
  },
].map((group) => ({
  ...group,
  types: group.typeIds
    .map((id) => typesById.get(id))
    .filter((t): t is NotificationTypeMeta => Boolean(t)),
}));

const NotificationSettings: React.FC = () => {
  const { success, error: showError } = useToast();
  const [preferences, setPreferences] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get('/users/me/notification-preferences');
        const prefsMap: Record<string, boolean> = {};
        (response.data as NotificationPreference[]).forEach((pref) => {
          prefsMap[pref.notification_type] = pref.enabled;
        });
        setPreferences(prefsMap);
      } catch (error) {
        console.error('Error fetching notification preferences:', error);
        showError('Failed to load notification preferences');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPreferences();
  }, [showError]);

  const handleToggle = (notificationType: string) => {
    setPreferences((prev) => ({
      ...prev,
      [notificationType]: !prev[notificationType],
    }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      await axios.patch('/users/me/notification-preferences', {
        preferences: preferences,
      });
      success('Notification preferences updated successfully');
    } catch (error: any) {
      console.error('Error updating notification preferences:', error);
      showError(error.response?.data?.detail || 'Failed to update notification preferences');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-8">
        {NOTIFICATION_GROUPS.map((group) => (
          <div key={group.title} role="group" aria-label={group.title} className="space-y-3">
            <div className="px-1">
              <div className="flex items-center gap-1.5">
                <group.icon className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {group.title}
                </h3>
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                {group.description}
              </p>
            </div>
            <div className="space-y-3">
              {group.types.map((type) => (
                <div
                  key={type.id}
                  className="flex items-center justify-between p-4 border border-gray-100 dark:border-gray-700/50 rounded-xl bg-white dark:bg-gray-800"
                >
                  <div className="flex-1 mr-4">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                      {type.label}
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {type.description}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={preferences[type.id] ?? true}
                    onClick={() => handleToggle(type.id)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                      preferences[type.id] ?? true
                        ? 'bg-primary-600'
                        : 'bg-gray-200 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        preferences[type.id] ?? true ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="pt-4 border-t border-gray-100 dark:border-gray-700/50">
        <Button
          onClick={handleSave}
          disabled={isSaving}
          variant="primary"
          size="lg"
        >
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default NotificationSettings;

